import { ReactNode, useEffect, useState } from "react";
import styles from "./Drawer2.module.scss";
import { XIcon } from "@/frontend/shared/assets/icons";
import clsx from "clsx";
import { Portal } from "../components/Portal";
import { Typography } from "@/frontend/shared/ui/Typography/Typography";
import { NormalButton, SquareButton } from "../../Button";
import { MODAL_ANIMATION_DELAY } from "../const/const";

export type TDrawer2Props = {
  className?: string;
  children: ReactNode;
  isOpen: boolean;
  position: "left" | "right" | "bottom";
  onClose: () => void;
  title?: string;
  onHeaderClick?: () => void;
};

export function Drawer2({ className, children, isOpen, onClose, position, title, onHeaderClick }: TDrawer2Props) {
  const [isOpening, setIsOpening] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    if (isOpen && !isMounted) {
      setIsOpening(true);
      setIsMounted(true);
      setTimeout(() => {
        setIsOpening(false);
      }, MODAL_ANIMATION_DELAY);
    } else if (!isOpen && isMounted) {
      setIsClosing(true);
      setTimeout(() => {
        setIsClosing(false);
        setIsMounted(false);
      }, MODAL_ANIMATION_DELAY);
    }
  }, [isOpen, isMounted]);

  if (!isMounted) return null;

  return (
    <Portal>
      <div
        className={clsx(
          styles.drawer,
          isMounted && styles.isOpen,
          isOpening && styles.isOpening,
          isClosing && styles.isClosing
        )}
      >
        <div
          className={clsx(styles.overlay)}
          onClick={(e) => {
            onClose?.();
            e.stopPropagation();
          }}
        >
          <div className={clsx(styles.content, styles[position])} onClick={(e) => e.stopPropagation()}>
            <div className={styles.headerContainer}>
              <Typography variant="heading-7" color="greyscale900" className={styles.title}>
                {title}
              </Typography>
              <div className={styles.cross}>
                {onHeaderClick && (
                  <NormalButton onClick={onHeaderClick} variant="tertiary" color="greyscale600" size="large">
                    Clear all
                  </NormalButton>
                )}
                <SquareButton
                  size="large"
                  variant="tertiary"
                  onClick={(e) => {
                    onClose?.();
                    e.stopPropagation();
                  }}
                >
                  <XIcon />
                </SquareButton>
              </div>
            </div>
            <div className={clsx(styles.contentBlock, className)}>{children}</div>
          </div>
        </div>
      </div>
    </Portal>
  );
}
